// Lettura generica di un file di import (JSON / XLSX / XLS / CSV) verso una
// forma tabellare unica `{ columns, rows }`, indipendente dall'entità di
// destinazione. Il mapping colonna → campo è a carico di ImportMappingModal.
import * as XLSX from 'xlsx';

/** Estensione (lowercase, senza punto) del file. */
export const fileExtension = (file) => (file?.name || '').split('.').pop()?.toLowerCase() || '';

/** True se il file è un workbook (XLSX/XLS/CSV) e può avere più fogli. */
export const isWorkbookFile = (file) => ['xlsx', 'xls', 'csv'].includes(fileExtension(file));

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target.result));
    reader.onerror = () => reject(new Error('read_error'));
    reader.readAsArrayBuffer(file);
  });
}

function readAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target.result ?? ''));
    reader.onerror = () => reject(new Error('read_error'));
    reader.readAsText(file);
  });
}

/** Legge il workbook e ne restituisce l'istanza XLSX (per elencare i fogli). */
export async function readWorkbook(file) {
  return XLSX.read(await readAsArrayBuffer(file), { type: 'array' });
}

/** Unione ordinata delle chiavi presenti nelle righe. */
function collectColumns(rows) {
  const cols = [];
  const seen = new Set();
  for (const row of rows) {
    for (const k of Object.keys(row || {})) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }
  }
  return cols;
}

/**
 * Individua la riga di intestazione: la prima con almeno due celle non vuote.
 * I tracciati reali hanno spesso titoli/note nelle prime righe.
 */
function detectHeaderRow(matrix) {
  for (let i = 0; i < Math.min(matrix.length, 30); i += 1) {
    const filled = (matrix[i] || []).filter((c) => c !== null && String(c).trim() !== '');
    if (filled.length >= 2) return i;
  }
  return 0;
}

/** Converte una cella XLSX in valore JS (le date seriali diventano `Date`). */
function cellValue(value) {
  if (value === undefined || value === '') return null;
  return value;
}

/** True se nessuna cella della riga porta un valore. */
const isBlankRow = (raw) => (raw || []).every((c) => c === null || String(c).trim() === '');

/**
 * Estrae `{ columns, rows, meta }` da un foglio del workbook.
 *
 * `meta[i]` accompagna `rows[i]` con il numero di riga del foglio (1-based,
 * quello mostrato da Excel) e il flag `blank`. Serve a due cose: citare la riga
 * giusta nel report errori e permettere ai tracciati che usano la riga vuota
 * come separatore di blocco di ritrovarla (vedi `keepEmptyRows`).
 *
 * @param {Object} workbook
 * @param {string} [sheetName]
 * @param {{ keepEmptyRows?: boolean }} [opts] Con `keepEmptyRows` le righe
 *   vuote restano in `rows` (oggetti con tutti i campi null) invece di essere
 *   scartate: i tracciati che separano i giri con una riga vuota perderebbero
 *   altrimenti l'unica informazione di raggruppamento.
 */
export function parseSheet(workbook, sheetName, opts = {}) {
  const sheet = workbook.Sheets[sheetName ?? workbook.SheetNames[0]];
  if (!sheet) return { columns: [], rows: [], meta: [] };

  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: null, raw: false });
  if (!matrix.length) return { columns: [], rows: [], meta: [] };

  const headerIndex = detectHeaderRow(matrix);
  const header = (matrix[headerIndex] || []).map((h, i) =>
    h === null || String(h).trim() === '' ? `col_${i + 1}` : String(h).trim()
  );

  const rows = [];
  const meta = [];
  for (let r = headerIndex + 1; r < matrix.length; r += 1) {
    const raw = matrix[r] || [];
    const blank = isBlankRow(raw);
    // Riga completamente vuota → scartata (i tracciati hanno righe separatrici),
    // salvo che il chiamante le usi come separatore di blocco.
    if (blank && !opts.keepEmptyRows) continue;
    const obj = {};
    header.forEach((name, c) => {
      obj[name] = cellValue(raw[c]);
    });
    rows.push(obj);
    meta.push({ sourceRow: r + 1, blank });
  }

  return { columns: header, rows, meta };
}

/**
 * Entry point unico. Per i workbook restituisce anche `sheets` e `workbook`,
 * così il chiamante può proporre il selettore foglio e ri-parsare senza
 * rileggere il file.
 *
 * @param {File} file
 * @param {{ sheetName?: string, workbook?: Object, keepEmptyRows?: boolean }} [opts]
 * @returns {Promise<{ columns: string[], rows: Object[], meta: Array<{sourceRow: number, blank: boolean}>, sheets?: string[], sheetName?: string, workbook?: Object }>}
 */
export async function parseImportFile(file, opts = {}) {
  if (!file) throw new Error('no_file');

  if (isWorkbookFile(file)) {
    const workbook = opts.workbook ?? (await readWorkbook(file));
    const sheets = workbook.SheetNames || [];
    const sheetName =
      opts.sheetName && sheets.includes(opts.sheetName) ? opts.sheetName : sheets[0];
    return { ...parseSheet(workbook, sheetName, opts), sheets, sheetName, workbook };
  }

  const text = await readAsText(file);
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('invalid_json');
  }

  // Accetta un array in radice oppure un oggetto che lo contiene
  // (`{ data: [...] }`, `{ items: [...] }`, `{ rows: [...] }`).
  const list = Array.isArray(parsed)
    ? parsed
    : [parsed?.data, parsed?.items, parsed?.rows, parsed?.records].find(Array.isArray);

  if (!Array.isArray(list)) throw new Error('invalid_shape');

  const rows = list.filter((r) => r && typeof r === 'object' && !Array.isArray(r));
  return {
    columns: collectColumns(rows),
    rows,
    meta: rows.map((_, i) => ({ sourceRow: i + 1, blank: false })),
  };
}
